import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getDashboardStats, seedCategories } from '@/lib/api';
import { toast } from 'sonner';
import { 
  FileText, 
  MessageSquare, 
  Mail, 
  HeartHandshake, 
  Users,
  Plus,
  RefreshCw
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSeedCategories = async () => {
    setSeeding(true);
    try {
      await seedCategories();
      toast.success('Categories seeded successfully!');
    } catch (error) {
      toast.error('Failed to seed categories');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-blue border-t-transparent"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Blog Posts',
      value: stats?.posts?.total || 0,
      subtitle: `${stats?.posts?.published || 0} published`,
      icon: FileText,
      color: 'bg-brand-blue',
      link: '/admin/blog'
    },
    {
      title: 'Pending Comments',
      value: stats?.pending_comments || 0,
      subtitle: 'Awaiting moderation',
      icon: MessageSquare,
      color: 'bg-brand-orange',
      link: '/admin/comments'
    },
    {
      title: 'Contact Submissions',
      value: stats?.contact_submissions || 0,
      subtitle: 'Total inquiries',
      icon: Mail,
      color: 'bg-brand-teal',
      link: '/admin/contacts'
    },
    {
      title: 'Support Requests',
      value: stats?.support_requests || 0,
      subtitle: 'Navigation requests',
      icon: HeartHandshake,
      color: 'bg-green-600',
      link: '/admin/support-requests'
    },
    {
      title: 'Partnership Inquiries',
      value: stats?.partnership_inquiries || 0,
      subtitle: 'Partnership interest',
      icon: Users,
      color: 'bg-purple-600',
      link: '/admin/partnerships'
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-brand-blue">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome to the admin dashboard</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={handleSeedCategories}
            disabled={seeding}
            variant="outline"
            className="border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white"
            data-testid="seed-categories-btn"
          >
            <RefreshCw size={18} className={`mr-2 ${seeding ? 'animate-spin' : ''}`} />
            Seed Categories
          </Button>
          <Link to="/admin/blog/new">
            <Button className="bg-brand-orange hover:bg-brand-orange/90 text-white" data-testid="new-post-btn">
              <Plus size={18} className="mr-2" />
              New Post
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card 
                className="bg-white rounded-xl hover:shadow-md transition-all cursor-pointer"
                data-testid={`stat-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">{stat.title}</p>
                      <p className="font-heading text-3xl font-bold text-brand-blue mt-2">{stat.value}</p>
                      <p className="text-gray-400 text-xs mt-1">{stat.subtitle}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                      <Icon size={24} className="text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="bg-white rounded-xl">
        <CardContent className="p-6">
          <h2 className="font-heading text-xl font-bold text-brand-blue mb-4">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/admin/blog/new">
              <Button variant="outline" className="w-full justify-start">
                <FileText size={18} className="mr-2" />
                Create Blog Post
              </Button>
            </Link>
            <Link to="/admin/comments">
              <Button variant="outline" className="w-full justify-start">
                <MessageSquare size={18} className="mr-2" />
                Moderate Comments
              </Button>
            </Link>
            <Link to="/admin/contacts">
              <Button variant="outline" className="w-full justify-start">
                <Mail size={18} className="mr-2" />
                View Contacts
              </Button>
            </Link>
            <Link to="/admin/support-requests">
              <Button variant="outline" className="w-full justify-start">
                <HeartHandshake size={18} className="mr-2" />
                Support Requests
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
